import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, brl } from "@/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CurrencyInput from "@/components/CurrencyInput";
import {
  Plus, Search, Phone, MessageCircle, Edit2, Trash2, Flame, Snowflake, Thermometer,
  ShoppingCart, BarChart3, Pin, PinOff, Clock, X, ChevronLeft, ChevronRight,
  Sparkles, User as UserIcon, MapPin, Tag as TagIcon, CalendarClock, Receipt,
  Eye, EyeOff, Calendar as CalendarIcon, StickyNote,
} from "lucide-react";

const TEMP_STYLES = {
  cold: { label: "Frio", icon: Snowflake, cls: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300" },
  warm: { label: "Morno", icon: Thermometer, cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  hot:  { label: "Quente", icon: Flame, cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
};

const SOURCES = ["Instagram", "Facebook", "WhatsApp", "Google", "Indicação", "Site", "Outro"];
const PAYMENT_METHODS = ["Pix", "Dinheiro", "Crédito", "Débito", "Boleto", "Outro"];

const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";

const waLink = (phone, msg = "") => {
  const num = String(phone || "").replace(/\D/g, "");
  if (!num) return null;
  const full = num.startsWith("55") ? num : "55" + num;
  return `https://wa.me/${full}${msg ? "?text=" + encodeURIComponent(msg) : ""}`;
};

export default function CRM() {
  const { canEditCRM } = useAuth();
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterTemp, setFilterTemp] = useState("");
  // Novo filtro: "all" (padrão) | "created" | "moved" — combinação de criação
  // ou movimentação de etapa dentro do intervalo de datas selecionado.
  const [dateField, setDateField] = useState("all");
  // ── Filtro de data: "all" (Todos), "month" (Por mês), "range" (Período)
  const [dateMode, setDateMode] = useState("all");
  const todayIso = new Date().toISOString().slice(0, 10);
  const [filterMonth, setFilterMonth] = useState(() => new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [editing, setEditing] = useState(null); // lead full
  const [openDetail, setOpenDetail] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [dragLeadId, setDragLeadId] = useState(null);
  const [hoverStageId, setHoverStageId] = useState(null);
  // Toggle global do "lucro previsto" exibido nos cards. Padrão: OCULTO
  // (vendedor/colaborador não pode ver custo do colega). Persistido por usuário.
  const SHOW_PROFIT_KEY = "gestaoexpert:crm:show_profit";
  const [showProfit, setShowProfit] = useState(() => {
    try { return localStorage.getItem(SHOW_PROFIT_KEY) === "1"; } catch { return false; }
  });
  const toggleProfit = () => {
    setShowProfit(prev => {
      const next = !prev;
      try { localStorage.setItem(SHOW_PROFIT_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  };
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [stagesOpen, setStagesOpen] = useState(false);
  const navigate = useNavigate();

  // Constrói os parâmetros de data com base no modo selecionado.
  const dateParams = useMemo(() => {
    if (dateMode === "month" && filterMonth) {
      // YYYY-MM → primeiro e último dia do mês (ISO yyyy-mm-dd)
      const [y, m] = filterMonth.split("-").map(Number);
      const first = `${y}-${String(m).padStart(2,"0")}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      const last = `${y}-${String(m).padStart(2,"0")}-${String(lastDay).padStart(2,"0")}`;
      return { created_from: first, created_to: last };
    }
    if (dateMode === "range" && (rangeFrom || rangeTo)) {
      const p = {};
      if (rangeFrom) p.created_from = rangeFrom;
      if (rangeTo) p.created_to = rangeTo;
      return p;
    }
    return {};
  }, [dateMode, filterMonth, rangeFrom, rangeTo]);

  const loadStages = async () => {
    const r = await api.get("/crm/stages");
    setStages(r.data);
  };
  const loadLeads = async () => {
    const r = await api.get("/crm/leads", { params: {
      q: search || undefined,
      source: filterSource || undefined,
      temperature: filterTemp || undefined,
      date_field: dateField || "all",
      ...dateParams,
    }});
    setLeads(r.data);
  };
  const loadDashboard = async () => {
    try {
      const r = await api.get("/crm/dashboard", { params: { date_field: dateField || "all", ...dateParams } });
      setDashboard(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadStages(); loadLeads(); }, []);
  useEffect(() => { const t = setTimeout(loadLeads, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, filterSource, filterTemp, dateField, dateParams]);
  useEffect(() => { if (dashboardOpen) loadDashboard(); /* eslint-disable-next-line */ }, [dashboardOpen, dateField, dateParams]);

  const leadsByStage = useMemo(() => {
    const m = {};
    stages.forEach(s => { m[s.id] = []; });
    leads.forEach(l => { if (m[l.stage_id]) m[l.stage_id].push(l); });
    return m;
  }, [stages, leads]);

  const moveLead = async (leadId, stageId) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead || lead.stage_id === stageId) return;
    // optimistic
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, stage_id: stageId } : l));
    try {
      await api.put(`/crm/leads/${leadId}/stage`, { stage_id: stageId });
      const stage = stages.find(s => s.id === stageId);
      if (stage?.is_won) toast.success("🎉 Venda concluída!");
    } catch (e) {
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, stage_id: lead.stage_id } : l));
      toast.error("Erro ao mover");
    }
  };

  const openLead = async (id) => {
    try {
      const r = await api.get(`/crm/leads/${id}`);
      setEditing(r.data); setOpenDetail(true);
    } catch { toast.error("Erro ao carregar lead"); }
  };

  const onDeleteLead = async (id, e) => {
    e?.stopPropagation();
    if (!canEditCRM) { toast.error("Acesso somente leitura"); return; }
    if (!confirm("Excluir este lead?")) return;
    await api.delete(`/crm/leads/${id}`);
    setLeads(prev => prev.filter(l => l.id !== id));
    toast.success("Lead excluído");
  };

  return (
    <div className="space-y-6" data-testid="crm-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-tiny mb-2">Relacionamento</p>
          <h1 className="font-display text-4xl tracking-tight">CRM</h1>
          <p className="text-sm text-neutral-500 mt-1">Funil completo do primeiro contato até a venda concluída.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={toggleProfit} title={showProfit ? "Ocultar lucros" : "Mostrar lucros"} data-testid="crm-toggle-profit">
            {showProfit ? <Eye size={14} className="mr-1.5"/> : <EyeOff size={14} className="mr-1.5"/>}
            Lucro
          </Button>
          <Button variant="outline" onClick={()=>setAgendaOpen(true)} data-testid="crm-agenda-button"><CalendarIcon size={14} className="mr-1.5"/> Agenda</Button>
          <Button variant="outline" onClick={()=>setDashboardOpen(true)} data-testid="crm-dashboard-button"><BarChart3 size={14} className="mr-1.5"/> Dashboard</Button>
          {canEditCRM && (
            <Button variant="outline" onClick={()=>setStagesOpen(true)} data-testid="crm-manage-stages-button"><TagIcon size={14} className="mr-1.5"/> Etapas</Button>
          )}
          {canEditCRM && (
            <Button onClick={()=>setOpenNew(true)} className="bg-[#0A0A0A] text-white hover:bg-black" data-testid="crm-new-lead-button"><Plus size={14} className="mr-1.5"/> Novo lead</Button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <Card className="p-4 border-[#E5E5E5] shadow-none">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto_auto] gap-3 items-end">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Buscar</div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
              <Input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Nome, telefone, cidade, interesse, produto, tag…" className="pl-9 h-9 text-sm" data-testid="crm-search"/>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Origem</div>
            <Select value={filterSource || "all"} onValueChange={(v)=>setFilterSource(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 h-9 text-xs" data-testid="crm-filter-source"><SelectValue placeholder="Origem"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as origens</SelectItem>
                {SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Filtro temperatura</div>
            <Select value={filterTemp || "all"} onValueChange={(v)=>setFilterTemp(v === "all" ? "" : v)}>
              <SelectTrigger className="w-36 h-9 text-xs" data-testid="crm-filter-temp"><SelectValue placeholder="Temperatura"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="hot">🔥 Quente</SelectItem>
                <SelectItem value="warm">🌡 Morno</SelectItem>
                <SelectItem value="cold">❄️ Frio</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Filtro data</div>
            <Select value={dateMode} onValueChange={(v)=>setDateMode(v)}>
              <SelectTrigger className="w-36 h-9 text-xs" data-testid="crm-filter-date-mode"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="month">Por mês</SelectItem>
                <SelectItem value="range">Período específico</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Filtro criação ou movimentação</div>
            <Select value={dateField} onValueChange={setDateField}>
              <SelectTrigger className="w-56 h-9 text-xs" data-testid="crm-filter-date-field"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos (criação + movimentação)</SelectItem>
                <SelectItem value="created">Criação</SelectItem>
                <SelectItem value="moved">Movimentação de etapa</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {(dateMode !== "all") && (
          <div className="mt-3 flex gap-2 items-end flex-wrap">
            {dateMode === "month" && (
              <Input
                type="month"
                value={filterMonth}
                onChange={e=>setFilterMonth(e.target.value)}
                className="w-40 h-9 text-xs"
                max={todayIso.slice(0,7)}
                data-testid="crm-filter-month"
              />
            )}
            {dateMode === "range" && (
              <>
                <Input type="date" value={rangeFrom} onChange={e=>setRangeFrom(e.target.value)} className="w-36 h-9 text-xs" placeholder="De" data-testid="crm-filter-from"/>
                <span className="text-xs text-neutral-500">até</span>
                <Input type="date" value={rangeTo} onChange={e=>setRangeTo(e.target.value)} className="w-36 h-9 text-xs" placeholder="Até" data-testid="crm-filter-to"/>
              </>
            )}
          </div>
        )}
      </Card>

      {/* KANBAN */}
      <div className="flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 snap-x" data-testid="crm-kanban">
        {stages.map(stage => {
          const items = leadsByStage[stage.id] || [];
          const totalValue = items.reduce((sum, l) => sum + Number(l.negotiation_value || 0), 0);
          const isHover = hoverStageId === stage.id;
          return (
            <div
              key={stage.id}
              className={`shrink-0 w-72 sm:w-80 snap-start rounded-xl border transition-all ${isHover ? "border-emerald-400 bg-emerald-50/30 dark:bg-emerald-950/20" : "border-[#E5E5E5] dark:border-white/10"}`}
              data-testid={`crm-stage-${stage.id}`}
              onDragOver={(e)=>{ e.preventDefault(); setHoverStageId(stage.id); }}
              onDragLeave={()=>setHoverStageId(null)}
              onDrop={(e)=>{ e.preventDefault(); setHoverStageId(null); if (dragLeadId) { moveLead(dragLeadId, stage.id); setDragLeadId(null); } }}
            >
              <div className="px-3 py-2.5 border-b border-[#E5E5E5] dark:border-white/10 flex items-center justify-between sticky top-0 bg-inherit rounded-t-xl">
                <StageHeader
                  stage={stage}
                  count={items.length}
                  totalValue={totalValue}
                  onRenamed={(newName)=>setStages(prev => prev.map(s => s.id === stage.id ? { ...s, name: newName } : s))}
                />
              </div>
              <div className="p-2 space-y-2 max-h-[calc(100vh-340px)] min-h-[60px] overflow-y-auto">
                {items.map(l => (
                  <LeadCard
                    key={l.id}
                    lead={l}
                    showProfit={showProfit}
                    onClick={()=>openLead(l.id)}
                    onDelete={(e)=>onDeleteLead(l.id, e)}
                    onDragStart={()=>setDragLeadId(l.id)}
                    onDragEnd={()=>setDragLeadId(null)}
                    onTouchDropOnStage={(stageId)=>moveLead(l.id, stageId)}
                  />
                ))}
                {items.length === 0 && (
                  <div className="text-center text-[11px] text-neutral-400 py-6 italic">Vazio — arraste leads aqui</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <NewLeadDialog
        open={openNew}
        onClose={()=>setOpenNew(false)}
        stages={stages}
        onCreated={(lead) => { setLeads(prev => [lead, ...prev]); toast.success("Lead criado"); }}
      />

      {editing && (
        <LeadDetailDialog
          open={openDetail}
          onClose={()=>{ setOpenDetail(false); setEditing(null); }}
          lead={editing}
          stages={stages}
          onUpdated={(updated)=>{
            setEditing(updated);
            setLeads(prev => prev.map(l => l.id === updated.id ? { ...l, ...updated, timeline: undefined, notes: undefined } : l));
          }}
          onDelete={()=>{ setLeads(prev => prev.filter(l => l.id !== editing.id)); setOpenDetail(false); setEditing(null); }}
          onFinalize={async () => {
            // Converte para customer e navega para Vendas com query param
            try {
              const r = await api.post(`/crm/leads/${editing.id}/convert-to-customer`);
              const cid = r.data.customer_id;
              const wonStage = stages.find(s => s.is_won);
              if (wonStage) {
                await api.put(`/crm/leads/${editing.id}/stage`, { stage_id: wonStage.id });
                setLeads(prev => prev.map(l => l.id === editing.id ? { ...l, stage_id: wonStage.id } : l));
              }
              setOpenDetail(false);
              toast.success("Cliente vinculado! Abrindo Vendas…");
              navigate(`/vendas?customer_id=${cid}&from_crm=${editing.id}`);
            } catch (e) { toast.error("Erro ao finalizar venda"); }
          }}
        />
      )}

      <DashboardDialog
        open={dashboardOpen}
        onClose={()=>setDashboardOpen(false)}
        data={dashboard}
        filtersSummary={{
          dateMode, filterMonth, rangeFrom, rangeTo,
          dateField, filterSource, filterTemp, search,
        }}
      />
      <AgendaDialog open={agendaOpen} onClose={()=>setAgendaOpen(false)} onOpenLead={openLead} />
      <StageManagerDialog
        open={stagesOpen}
        onClose={()=>setStagesOpen(false)}
        stages={stages}
        onChanged={async () => { await loadStages(); await loadLeads(); }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARD do lead
// Suporta Touch DnD além do HTML5 drag/drop para funcionar no mobile.
// A estratégia: no touchstart, marcamos o card e seguimos o dedo até soltar.
// No touchend, descobrimos qual coluna está sob o ponto e disparamos onTouchDropOnStage.
// ─────────────────────────────────────────────────────────────────────────────
function StageHeader({ stage, count, totalValue, onRenamed }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(stage.name);
  useEffect(() => { setVal(stage.name); }, [stage.name]);
  const save = async () => {
    const newName = (val || "").trim();
    if (!newName || newName === stage.name) { setEditing(false); setVal(stage.name); return; }
    try {
      await api.put(`/crm/stages/${stage.id}`, { name: newName, color: stage.color });
      onRenamed(newName);
      toast.success("Etapa renomeada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao renomear");
      setVal(stage.name);
    } finally { setEditing(false); }
  };
  if (editing) {
    return (
      <div className="flex items-center gap-2 min-w-0 w-full">
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: stage.color }}/>
        <input
          autoFocus
          className="text-sm font-medium bg-transparent border-b border-emerald-400 focus:outline-none flex-1 min-w-0"
          value={val}
          onChange={e=>setVal(e.target.value)}
          onBlur={save}
          onKeyDown={(e)=>{
            if (e.key === "Enter") save();
            else if (e.key === "Escape") { setEditing(false); setVal(stage.name); }
          }}
          data-testid={`stage-rename-input-${stage.id}`}
        />
      </div>
    );
  }
  return (
    <>
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: stage.color }}/>
        <button
          type="button"
          onClick={()=>setEditing(true)}
          title="Clique para renomear"
          className="text-sm font-medium truncate hover:text-emerald-600 text-left"
          data-testid={`stage-name-${stage.id}`}
        >{stage.name}</button>
        <span className="text-[10px] text-neutral-500 bg-neutral-100 dark:bg-white/5 rounded-full px-1.5 py-0.5">{count}</span>
      </div>
      {totalValue > 0 && <span className="text-[10px] font-mono text-neutral-500 shrink-0">{brl(totalValue)}</span>}
    </>
  );
}

function LeadCard({ lead, showProfit, onClick, onDelete, onDragStart, onDragEnd, onTouchDropOnStage }) {
  const temp = TEMP_STYLES[lead.temperature || "warm"];
  const TempIcon = temp.icon;
  const wa = waLink(lead.whatsapp || lead.phone);
  const ref = useRef(null);
  const touchState = useRef({ moving: false, startX: 0, startY: 0, ghost: null, currentStage: null });

  // texto da última/fixada observação (vem do backend `pinned_note_text` ou `last_note_text`)
  const noteText = lead.pinned_note_text || lead.last_note_text || "";
  const isPinned = !!lead.pinned_note_text;

  const profit = (Number(lead.negotiation_value) || 0) - (Number(lead.cost_value) || 0);

  // Próximo contato agendado
  const nextContact = lead.next_contact_at ? new Date(lead.next_contact_at) : null;
  const nextOverdue = nextContact && nextContact < new Date();

  const onTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    touchState.current = { moving: false, startX: t.clientX, startY: t.clientY, ghost: null, currentStage: null };
  };
  const onTouchMove = (e) => {
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    const dx = t.clientX - touchState.current.startX;
    const dy = t.clientY - touchState.current.startY;
    if (!touchState.current.moving && Math.hypot(dx, dy) < 8) return;
    // Inicia drag
    if (!touchState.current.moving) {
      touchState.current.moving = true;
      // Cria um clone "ghost" seguindo o dedo
      const rect = ref.current.getBoundingClientRect();
      const ghost = ref.current.cloneNode(true);
      ghost.style.position = "fixed";
      ghost.style.left = `${rect.left}px`;
      ghost.style.top = `${rect.top}px`;
      ghost.style.width = `${rect.width}px`;
      ghost.style.pointerEvents = "none";
      ghost.style.opacity = "0.85";
      ghost.style.transform = "scale(1.02) rotate(-1deg)";
      ghost.style.zIndex = "9999";
      ghost.style.boxShadow = "0 10px 30px rgba(0,0,0,0.25)";
      document.body.appendChild(ghost);
      touchState.current.ghost = ghost;
      ref.current.style.opacity = "0.35";
      // Bloqueia scroll vertical da página
      document.body.style.overflow = "hidden";
    }
    e.preventDefault();
    const ghost = touchState.current.ghost;
    if (ghost) {
      ghost.style.left = `${t.clientX - 100}px`;
      ghost.style.top = `${t.clientY - 30}px`;
    }
    // Detecta sobre qual stage está
    const under = document.elementFromPoint(t.clientX, t.clientY);
    const stageEl = under?.closest('[data-testid^="crm-stage-"]');
    const sid = stageEl ? stageEl.getAttribute("data-testid").replace("crm-stage-", "") : null;
    // Highlight visual em mobile
    if (sid !== touchState.current.currentStage) {
      document.querySelectorAll('[data-testid^="crm-stage-"]').forEach(el => el.classList.remove("ring-2", "ring-emerald-400"));
      if (stageEl) stageEl.classList.add("ring-2", "ring-emerald-400");
      touchState.current.currentStage = sid;
    }
  };
  const onTouchEnd = () => {
    const wasMoving = touchState.current.moving;
    const target = touchState.current.currentStage;
    // Cleanup
    if (touchState.current.ghost) { touchState.current.ghost.remove(); touchState.current.ghost = null; }
    if (ref.current) ref.current.style.opacity = "";
    document.body.style.overflow = "";
    document.querySelectorAll('[data-testid^="crm-stage-"]').forEach(el => el.classList.remove("ring-2", "ring-emerald-400"));
    touchState.current.moving = false;
    if (wasMoving && target && target !== lead.stage_id) {
      onTouchDropOnStage && onTouchDropOnStage(target);
    }
  };

  return (
    <div
      ref={ref}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onClick={(e)=>{ if (!touchState.current.moving) onClick(); }}
      style={{ touchAction: "pan-y" }}
      className="group bg-white dark:bg-white/[0.03] rounded-lg border border-[#E5E5E5] dark:border-white/10 p-3 cursor-pointer hover:border-emerald-400 hover:shadow-sm transition active:cursor-grabbing select-none"
      data-testid={`crm-lead-${lead.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-medium ${temp.cls}`}>
              <TempIcon size={9}/> {temp.label}
            </span>
            {(lead.tags || []).slice(0, 1).map(t => (
              <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-white/10 text-neutral-600 dark:text-neutral-300 truncate max-w-[60px]">{t}</span>
            ))}
            {nextContact && (
              <span className={`inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-medium ${nextOverdue ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" : "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"}`} title={`Próximo contato: ${fmtDateTime(lead.next_contact_at)}`}>
                <CalendarClock size={9}/> {nextContact.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
              </span>
            )}
          </div>
          <h3 className="font-medium text-sm mt-1.5 truncate">{lead.name}</h3>
          {lead.interest && <p className="text-[11px] text-neutral-500 truncate mt-0.5">{lead.interest}</p>}
        </div>
      </div>

      {/* Última/Pinned obs no card */}
      {noteText && (
        <div className={`mt-2 text-[11px] px-2 py-1.5 rounded border-l-2 ${isPinned ? "border-amber-400 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200" : "border-neutral-300 bg-neutral-50 dark:bg-white/[0.03] text-neutral-600 dark:text-neutral-400"} line-clamp-2`} title={noteText}>
          <span className="inline-flex items-center gap-1">
            {isPinned ? <Pin size={9}/> : <StickyNote size={9}/>}
            <span className="truncate">{noteText}</span>
          </span>
        </div>
      )}

      {Number(lead.negotiation_value) > 0 && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="font-display text-base">{brl(lead.negotiation_value)}</div>
          {showProfit && (
            <div className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium font-mono">+ {brl(profit)}</div>
          )}
        </div>
      )}

      <div className="mt-2 flex items-center justify-between text-[10px] text-neutral-400">
        <span className="truncate">{lead.city || lead.source || "—"}</span>
        <span className="shrink-0">{fmtDateTime(lead.last_contact_at || lead.updated_at).split(" ")[0]}</span>
      </div>

      <div className="mt-2 pt-2 border-t border-[#F0F0F0] dark:border-white/5 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition" onClick={e=>e.stopPropagation()}>
        {wa && (
          <a href={wa} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} className="p-1.5 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded text-emerald-600" title="WhatsApp" data-testid={`crm-card-wa-${lead.id}`}>
            <MessageCircle size={13}/>
          </a>
        )}
        {lead.phone && (
          <a href={`tel:${lead.phone}`} onClick={e=>e.stopPropagation()} className="p-1.5 hover:bg-neutral-100 dark:hover:bg-white/5 rounded" title="Ligar">
            <Phone size={13}/>
          </a>
        )}
        <button onClick={onClick} className="p-1.5 hover:bg-neutral-100 dark:hover:bg-white/5 rounded" title="Abrir"><Edit2 size={13}/></button>
        <button onClick={onDelete} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 rounded text-red-500" title="Excluir"><Trash2 size={13}/></button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dialog: criar lead
// Suporta selecionar um cliente já cadastrado para auto-preencher os dados.
// ─────────────────────────────────────────────────────────────────────────────
function NewLeadDialog({ open, onClose, stages, onCreated }) {
  const empty = {
    name: "", phone: "", whatsapp: "", email: "", city: "", address: "", cpf: "",
    interest: "", negotiation_value: 0, source: "", temperature: "warm",
    stage_id: stages[0]?.id, customer_id: null,
  };
  const [form, setForm] = useState(empty);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustList, setShowCustList] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...empty, stage_id: stages[0]?.id });
    setSelectedCustomer(null);
    setCustomerSearch("");
    setShowCustList(false);
    // carrega base de clientes para o seletor
    api.get("/customers").then(r => setCustomers(r.data || [])).catch(() => {});
    /* eslint-disable-next-line */
  }, [open]);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Refs para implementar click-outside (fechar dropdown ao clicar fora)
  const custWrapRef = useRef(null);
  useEffect(() => {
    if (!showCustList) return;
    const onDocDown = (e) => {
      if (custWrapRef.current && !custWrapRef.current.contains(e.target)) {
        setShowCustList(false);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("touchstart", onDocDown);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("touchstart", onDocDown);
    };
  }, [showCustList]);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    const base = q
      ? customers.filter(c =>
          (c.name || "").toLowerCase().includes(q) ||
          (c.phone || "").includes(q) ||
          (c.cpf || "").includes(q)
        )
      : customers;
    return base.slice(0, 20);
  }, [customerSearch, customers]);

  const pickCustomer = (c) => {
    setSelectedCustomer(c);
    setShowCustList(false);
    setCustomerSearch(c.name);
    setForm(p => ({
      ...p,
      name: c.name || p.name,
      phone: c.phone || p.phone,
      whatsapp: c.phone || p.whatsapp,
      cpf: c.cpf || p.cpf,
      address: c.address || p.address,
      customer_id: c.id,
    }));
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearch("");
    setForm(p => ({ ...p, customer_id: null }));
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nome obrigatório"); return; }
    setSaving(true);
    try {
      const r = await api.post("/crm/leads", form);
      onCreated(r.data); onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Erro ao criar lead";
      toast.error(typeof msg === "string" ? msg : "Erro ao criar lead");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="new-lead-dialog">
        <DialogHeader><DialogTitle className="font-display">Novo lead</DialogTitle></DialogHeader>

        {/* Seletor de cliente existente */}
        <div className="rounded-lg border border-dashed border-[#E5E5E5] dark:border-white/10 p-3 bg-neutral-50/60 dark:bg-white/[0.02]">
          <Label className="flex items-center gap-1.5 mb-1.5">
            <UserIcon size={13}/> Vincular cliente existente <span className="text-[10px] text-neutral-400 font-normal">(opcional)</span>
          </Label>
          {selectedCustomer ? (
            <div className="flex items-center justify-between gap-2 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 rounded-md px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{selectedCustomer.name}</div>
                <div className="text-[11px] text-neutral-500 truncate">
                  {selectedCustomer.phone || "—"}{selectedCustomer.cpf ? ` · CPF ${selectedCustomer.cpf}` : ""}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={clearCustomer} className="shrink-0 text-xs h-7" data-testid="new-lead-clear-customer">
                <X size={12} className="mr-1"/> Remover
              </Button>
            </div>
          ) : (
            <div className="relative" ref={custWrapRef}>
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"/>
              <Input
                value={customerSearch}
                onChange={e=>{ setCustomerSearch(e.target.value); setShowCustList(e.target.value.length > 0); }}
                placeholder="Buscar por nome, telefone ou CPF…"
                className="pl-8 pr-8 h-9 text-sm"
                data-testid="new-lead-customer-search"
              />
              {customerSearch && (
                <button
                  type="button"
                  onClick={()=>{ setCustomerSearch(""); setShowCustList(false); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-neutral-400 hover:text-neutral-700"
                  aria-label="Limpar busca"
                  data-testid="new-lead-customer-clear-search"
                >
                  <X size={14}/>
                </button>
              )}
              {showCustList && customerSearch.trim() && filteredCustomers.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white dark:bg-[#0A0A0A] border border-[#E5E5E5] dark:border-white/10 rounded-md shadow-lg">
                  {filteredCustomers.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={()=>pickCustomer(c)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 dark:hover:bg-emerald-950/30 border-b border-[#F0F0F0] dark:border-white/5 last:border-0"
                      data-testid={`new-lead-pick-customer-${c.id}`}
                    >
                      <div className="font-medium truncate">{c.name}</div>
                      <div className="text-[11px] text-neutral-500 truncate">{c.phone || "—"}{c.cpf ? ` · ${c.cpf}` : ""}</div>
                    </button>
                  ))}
                </div>
              )}
              {showCustList && customerSearch.trim() && filteredCustomers.length === 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white dark:bg-[#0A0A0A] border border-[#E5E5E5] dark:border-white/10 rounded-md p-3 text-xs text-neutral-400 italic">
                  Nenhum cliente encontrado.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label>Nome *</Label><Input value={form.name} onChange={e=>update("name", e.target.value)} data-testid="new-lead-name"/></div>
          <div><Label>Telefone</Label><Input value={form.phone} onChange={e=>update("phone", e.target.value)} data-testid="new-lead-phone"/></div>
          <div><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={e=>update("whatsapp", e.target.value)} placeholder="(se diferente do tel.)"/></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={e=>update("email", e.target.value)}/></div>
          <div><Label>Cidade</Label><Input value={form.city} onChange={e=>update("city", e.target.value)}/></div>
          <div className="sm:col-span-2"><Label>Interesse</Label><Input value={form.interest} onChange={e=>update("interest", e.target.value)} placeholder="Vara fly, kit camping, etc"/></div>
          <div><Label>Valor estimado</Label><CurrencyInput value={form.negotiation_value} onChange={(v)=>update("negotiation_value", v)}/></div>
          <div>
            <Label>Origem</Label>
            <Select value={form.source || ""} onValueChange={(v)=>update("source", v)}>
              <SelectTrigger><SelectValue placeholder="Origem do lead"/></SelectTrigger>
              <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Temperatura</Label>
            <Select value={form.temperature} onValueChange={(v)=>update("temperature", v)}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="hot">🔥 Quente</SelectItem>
                <SelectItem value="warm">🌡 Morno</SelectItem>
                <SelectItem value="cold">❄️ Frio</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Etapa inicial</Label>
            <Select value={form.stage_id || ""} onValueChange={(v)=>update("stage_id", v)}>
              <SelectTrigger data-testid="new-lead-stage-trigger"><SelectValue/></SelectTrigger>
              <SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0A0A0A] text-white hover:bg-black" data-testid="new-lead-submit">
            {saving ? "Criando…" : "Criar lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dialog: detalhe completo + timeline + notas + finalizar venda
// ─────────────────────────────────────────────────────────────────────────────
function LeadDetailDialog({ open, onClose, lead, stages, onUpdated, onDelete, onFinalize }) {
  const [form, setForm] = useState(lead);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(lead), [lead]);
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const wa = waLink(form.whatsapp || form.phone);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/crm/leads/${lead.id}`, form);
      onUpdated(r.data); toast.success("Salvo");
    } catch { toast.error("Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const addNote = async () => {
    const t = noteText.trim();
    if (!t) return;
    try {
      await api.post(`/crm/leads/${lead.id}/notes`, { text: t });
      const r = await api.get(`/crm/leads/${lead.id}`); onUpdated(r.data); setNoteText("");
    } catch { toast.error("Erro"); }
  };

  const togglePin = async (note) => {
    try {
      await api.put(`/crm/leads/${lead.id}/notes/${note.id}`, { pinned: !note.pinned });
      const r = await api.get(`/crm/leads/${lead.id}`); onUpdated(r.data);
    } catch { toast.error("Erro"); }
  };

  const delNote = async (id) => {
    if (!confirm("Excluir observação?")) return;
    try {
      await api.delete(`/crm/leads/${lead.id}/notes/${id}`);
      const r = await api.get(`/crm/leads/${lead.id}`); onUpdated(r.data);
    } catch { toast.error("Erro"); }
  };

  const remove = async () => {
    if (!confirm("Excluir este lead? Esta ação não pode ser desfeita.")) return;
    await api.delete(`/crm/leads/${lead.id}`); onDelete();
  };

  const markContact = async () => {
    await api.post(`/crm/leads/${lead.id}/contact-now`);
    const r = await api.get(`/crm/leads/${lead.id}`); onUpdated(r.data); toast.success("Contato registrado");
  };

  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="lead-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            <UserIcon size={18}/> {form.name || "Sem nome"}
            <span className="text-xs text-neutral-500 font-sans font-normal">criado {fmtDateTime(form.created_at)}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 mb-3">
          {wa && <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs px-3 h-9 rounded-md bg-emerald-600 text-white hover:bg-emerald-700" data-testid="lead-wa-button"><MessageCircle size={14}/> WhatsApp</a>}
          {form.phone && <a href={`tel:${form.phone}`} className="inline-flex items-center gap-1.5 text-xs px-3 h-9 rounded-md border hover:bg-neutral-50 dark:hover:bg-white/5"><Phone size={14}/> Ligar</a>}
          <Button size="sm" variant="outline" onClick={markContact} className="text-xs"><CalendarClock size={13} className="mr-1"/> Marcar contato agora</Button>
          <Button size="sm" onClick={onFinalize} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="lead-finalize-button"><ShoppingCart size={13} className="mr-1"/> Finalizar venda</Button>
        </div>

        <Tabs defaultValue="info">
          <TabsList>
            <TabsTrigger value="info">Dados</TabsTrigger>
            <TabsTrigger value="commercial">Negociação</TabsTrigger>
            <TabsTrigger value="notes">Observações ({(form.notes || []).length})</TabsTrigger>
            <TabsTrigger value="timeline">Timeline ({(form.timeline || []).length})</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="mt-4 grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Nome</Label><Input value={form.name || ""} onChange={e=>u("name", e.target.value)}/></div>
            <div><Label>Telefone</Label><Input value={form.phone || ""} onChange={e=>u("phone", e.target.value)}/></div>
            <div><Label>WhatsApp</Label><Input value={form.whatsapp || ""} onChange={e=>u("whatsapp", e.target.value)}/></div>
            <div><Label>Email</Label><Input value={form.email || ""} onChange={e=>u("email", e.target.value)}/></div>
            <div><Label>CPF</Label><Input value={form.cpf || ""} onChange={e=>u("cpf", e.target.value)}/></div>
            <div><Label>Cidade</Label><Input value={form.city || ""} onChange={e=>u("city", e.target.value)}/></div>
            <div className="sm:col-span-2"><Label>Endereço</Label><Input value={form.address || ""} onChange={e=>u("address", e.target.value)}/></div>
            <div>
              <Label>Etapa</Label>
              <Select value={form.stage_id || ""} onValueChange={async (v) => {
                u("stage_id", v);
                await api.put(`/crm/leads/${lead.id}/stage`, { stage_id: v });
                const r = await api.get(`/crm/leads/${lead.id}`); onUpdated(r.data);
              }}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Temperatura</Label>
              <Select value={form.temperature || "warm"} onValueChange={(v)=>u("temperature", v)}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hot">🔥 Quente</SelectItem>
                  <SelectItem value="warm">🌡 Morno</SelectItem>
                  <SelectItem value="cold">❄️ Frio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Tags (separadas por vírgula)</Label>
              <Input
                value={(form.tags || []).join(", ")}
                onChange={e=>u("tags", e.target.value.split(",").map(t=>t.trim()).filter(Boolean))}
                placeholder="VIP, indicação, recorrente…"
              />
            </div>
          </TabsContent>

          <TabsContent value="commercial" className="mt-4 grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Interesse</Label><Input value={form.interest || ""} onChange={e=>u("interest", e.target.value)}/></div>
            <div className="sm:col-span-2"><Label>Produtos desejados</Label><Textarea rows={2} value={form.products_desired || ""} onChange={e=>u("products_desired", e.target.value)}/></div>
            <div><Label>Valor da negociação</Label><CurrencyInput value={form.negotiation_value || 0} onChange={(v)=>u("negotiation_value", v)}/></div>
            <div><Label>Custo (interno)</Label><CurrencyInput value={form.cost_value || 0} onChange={(v)=>u("cost_value", v)}/></div>
            <div><Label>Desconto</Label><CurrencyInput value={form.discount || 0} onChange={(v)=>u("discount", v)}/></div>
            <div>
              <Label>Forma de pagamento</Label>
              <Select value={form.payment_method || ""} onValueChange={(v)=>u("payment_method", v)}>
                <SelectTrigger><SelectValue placeholder="Selecionar"/></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Data prevista de compra</Label><Input type="date" value={form.expected_purchase_date || ""} onChange={e=>u("expected_purchase_date", e.target.value)}/></div>
            <div>
              <Label className="flex items-center gap-1.5"><CalendarClock size={13}/> Agendar próximo contato</Label>
              <Input
                type="datetime-local"
                value={(() => {
                  // ISO (UTC com 'Z') precisa virar formato local YYYY-MM-DDTHH:mm
                  // pro <input type="datetime-local"> exibir corretamente.
                  if (!form.next_contact_at) return "";
                  const d = new Date(form.next_contact_at);
                  if (isNaN(d.getTime())) return "";
                  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
                  return local.toISOString().slice(0, 16);
                })()}
                onChange={e=>u("next_contact_at", e.target.value ? new Date(e.target.value).toISOString() : null)}
                data-testid="lead-next-contact"
              />
              <p className="text-[10px] text-neutral-500 mt-1">Aparece como badge no card e na aba "Agenda".</p>
            </div>
            <div>
              <Label>Origem</Label>
              <Select value={form.source || ""} onValueChange={(v)=>u("source", v)}>
                <SelectTrigger><SelectValue placeholder="Origem"/></SelectTrigger>
                <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 text-xs text-neutral-500 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/50 rounded p-2">
              <strong>Lucro previsto:</strong> {brl((Number(form.negotiation_value)||0) - (Number(form.cost_value)||0))}
            </div>
          </TabsContent>

          <TabsContent value="notes" className="mt-4 space-y-3">
            <div className="flex gap-2">
              <Textarea rows={2} value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="Cliente pediu desconto, retornar amanhã…" data-testid="lead-note-input"/>
              <Button onClick={addNote} className="bg-[#0A0A0A] text-white shrink-0" data-testid="lead-add-note">Adicionar</Button>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {(form.notes || []).slice().reverse().map(n => (
                <div key={n.id} className={`rounded-md p-3 text-sm border ${n.pinned ? "border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30" : "border-[#E5E5E5] dark:border-white/10"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="flex-1 whitespace-pre-line">{n.text}</p>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={()=>togglePin(n)} className="p-1 hover:bg-white/50 rounded" title={n.pinned ? "Desafixar" : "Fixar"}>
                        {n.pinned ? <PinOff size={12}/> : <Pin size={12}/>}
                      </button>
                      <button onClick={()=>delNote(n.id)} className="p-1 hover:bg-red-50 dark:hover:bg-red-900/30 rounded text-red-500"><Trash2 size={12}/></button>
                    </div>
                  </div>
                  <div className="mt-1.5 text-[10px] text-neutral-400">{n.by} · {fmtDateTime(n.at)}</div>
                </div>
              ))}
              {(form.notes || []).length === 0 && <p className="text-xs text-neutral-400 italic text-center py-8">Sem observações ainda.</p>}
            </div>
          </TabsContent>

          <TabsContent value="timeline" className="mt-4">
            <div className="space-y-2 max-h-96 overflow-y-auto relative pl-5 border-l border-[#E5E5E5] dark:border-white/10">
              {(form.timeline || []).slice().reverse().map(ev => (
                <div key={ev.id} className="relative">
                  <span className="absolute -left-[27px] top-1 w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-[#0A0A0A]"/>
                  <div className="bg-neutral-50 dark:bg-white/[0.03] rounded p-2.5 text-xs">
                    <div className="font-medium">{ev.label}</div>
                    <div className="text-[10px] text-neutral-400 mt-1">{ev.by} · {fmtDateTime(ev.at)}</div>
                  </div>
                </div>
              ))}
              {(form.timeline || []).length === 0 && <p className="text-xs text-neutral-400 italic">Sem eventos.</p>}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={remove} className="text-red-500 mr-auto"><Trash2 size={14} className="mr-1"/> Excluir lead</Button>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0A0A0A] text-white" data-testid="lead-save">{saving ? "Salvando…" : "Salvar alterações"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard
// ─────────────────────────────────────────────────────────────────────────────
function DashboardDialog({ open, onClose, data, filtersSummary }) {
  const filtersLabel = useMemo(() => {
    if (!filtersSummary) return null;
    const parts = [];
    const { dateMode, filterMonth, rangeFrom, rangeTo, dateField, filterSource, filterTemp, search } = filtersSummary;
    if (dateMode === "month" && filterMonth) {
      const [y, m] = filterMonth.split("-");
      const names = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
      parts.push(`${names[Number(m)-1]}/${y}`);
    } else if (dateMode === "range" && (rangeFrom || rangeTo)) {
      parts.push(`${rangeFrom || "…"} → ${rangeTo || "…"}`);
    } else {
      parts.push("Todo o período");
    }
    if (dateField === "created") parts.push("apenas criação");
    else if (dateField === "moved") parts.push("com interação (criação ou movimentação)");
    else parts.push("criação + movimentação");
    if (filterSource) parts.push(`origem: ${filterSource}`);
    if (filterTemp) {
      const t = ({ hot: "🔥 Quente", warm: "🌡 Morno", cold: "❄️ Frio" })[filterTemp] || filterTemp;
      parts.push(`temp.: ${t}`);
    }
    if (search) parts.push(`busca: "${search}"`);
    return parts;
  }, [filtersSummary]);

  if (!data) return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent><DialogHeader><DialogTitle>Dashboard CRM</DialogTitle></DialogHeader><p className="text-sm text-neutral-500">Carregando…</p></DialogContent>
    </Dialog>
  );
  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><BarChart3 size={18}/> Dashboard CRM</DialogTitle></DialogHeader>
        {filtersLabel && filtersLabel.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 -mt-2 mb-2" data-testid="dashboard-filters-summary">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500">Filtros aplicados:</span>
            {filtersLabel.map((t, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-white/10 text-neutral-700 dark:text-neutral-200">{t}</span>
            ))}
          </div>
        )}
        <div className="grid sm:grid-cols-4 gap-3">
          {[
            { k: "Total de leads", v: data.total_leads, kind: "n" },
            { k: "Vendas concluídas", v: data.total_won, kind: "n" },
            { k: "Conversão", v: `${data.conversion_rate}%`, kind: "%" },
            { k: "Tempo médio fechamento", v: `${data.avg_close_days}d`, kind: "d" },
            { k: "Valor em negociação", v: brl(data.total_value), kind: "$" },
            { k: "Valor vendido", v: brl(data.total_won_value), kind: "$" },
            { k: "Lucro total", v: brl(data.total_won_profit), kind: "$", green: true },
            { k: "Ticket médio", v: brl(data.avg_ticket), kind: "$" },
          ].map(m => (
            <Card key={m.k} className={`p-4 border-[#E5E5E5] shadow-none ${m.green ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-100 dark:border-emerald-900/50" : ""}`}>
              <p className="text-[10px] uppercase tracking-widest text-neutral-500">{m.k}</p>
              <div className={`font-display text-2xl mt-1 ${m.green ? "text-emerald-700 dark:text-emerald-400" : ""}`}>{m.v}</div>
            </Card>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mt-6">
          <Card className="p-5 border-[#E5E5E5] shadow-none">
            <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3">Funil por etapa</p>
            <div className="space-y-2">
              {data.by_stage.map((s, i) => {
                const maxCount = Math.max(...data.by_stage.map(x => x.count), 1);
                const pct = (s.count / maxCount) * 100;
                return (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: s.color }}/> {s.name}</span>
                      <span className="text-neutral-500">{s.count} · {brl(s.value)}</span>
                    </div>
                    <div className="h-1.5 bg-neutral-100 dark:bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${pct}%`, background: s.color }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="p-5 border-[#E5E5E5] shadow-none">
            <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3">Origem dos leads</p>
            <ul className="space-y-2">
              {data.by_source.slice(0, 8).map(s => (
                <li key={s.source} className="flex justify-between text-sm">
                  <span>{s.source}</span><span className="font-mono">{s.count}</span>
                </li>
              ))}
              {data.by_source.length === 0 && <li className="text-xs text-neutral-400">Sem dados.</li>}
            </ul>
          </Card>
        </div>

        <Card className="p-5 border-[#E5E5E5] shadow-none mt-6">
          <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3">Ranking de vendedores</p>
          {data.ranking.length === 0 && <p className="text-xs text-neutral-400">Sem vendas ainda.</p>}
          {data.ranking.map((r, i) => (
            <div key={r.name} className="flex items-center justify-between text-sm py-2 border-b border-[#F0F0F0] dark:border-white/5 last:border-0">
              <span className="flex items-center gap-2"><span className="text-neutral-400 font-mono text-xs">{i + 1}º</span> {r.name}</span>
              <span className="text-xs text-neutral-500">{r.won} vendas · <span className="text-emerald-600 dark:text-emerald-400 font-medium">{brl(r.value)}</span></span>
            </div>
          ))}
        </Card>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dialog: Agenda — lista próximos contatos agrupados por dia
// ─────────────────────────────────────────────────────────────────────────────
function AgendaDialog({ open, onClose, onOpenLead }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/crm/agenda")
      .then(r => setItems(r.data))
      .catch(() => toast.error("Erro ao carregar agenda"))
      .finally(() => setLoading(false));
  }, [open]);

  // Agrupa por dia (YYYY-MM-DD local) — facilita scaneamento visual
  const grouped = useMemo(() => {
    const map = {};
    for (const lead of items) {
      if (!lead.next_contact_at) continue;
      const d = new Date(lead.next_contact_at);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
      if (!map[key]) map[key] = [];
      map[key].push(lead);
    }
    return Object.entries(map).sort(([a],[b]) => a.localeCompare(b));
  }, [items]);

  const today = new Date(); today.setHours(0,0,0,0);
  const labelForDay = (key) => {
    const [y, m, d] = key.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    const diffDays = Math.round((dt - today) / 86400000);
    if (diffDays === 0) return "Hoje";
    if (diffDays === 1) return "Amanhã";
    if (diffDays === -1) return "Ontem";
    if (diffDays < 0) return `Atrasado · ${dt.toLocaleDateString("pt-BR")}`;
    return dt.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "2-digit" });
  };

  const markContact = async (lead) => {
    try {
      await api.post(`/crm/leads/${lead.id}/contact-now`);
      // Remove o próximo contato pra sair da agenda
      await api.put(`/crm/leads/${lead.id}`, { next_contact_at: null });
      setItems(prev => prev.filter(l => l.id !== lead.id));
      toast.success("Contato marcado como realizado");
    } catch { toast.error("Erro"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="agenda-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><CalendarIcon size={18}/> Agenda de contatos</DialogTitle>
        </DialogHeader>
        {loading && <p className="text-sm text-neutral-500">Carregando…</p>}
        {!loading && grouped.length === 0 && (
          <p className="text-sm text-neutral-500 text-center py-12 italic">
            Nenhum contato agendado.<br/>
            <span className="text-xs">Abra um lead → aba Negociação → "Agendar próximo contato".</span>
          </p>
        )}
        <div className="space-y-5">
          {grouped.map(([day, leads]) => {
            const isOverdue = (() => {
              const [y, m, d] = day.split("-").map(Number);
              return new Date(y, m - 1, d) < today;
            })();
            return (
              <div key={day}>
                <div className={`text-xs font-semibold uppercase tracking-widest mb-2 ${isOverdue ? "text-red-500" : "text-emerald-700 dark:text-emerald-400"}`}>
                  {labelForDay(day)} <span className="text-neutral-400 font-normal">· {leads.length} contato{leads.length !== 1 ? "s" : ""}</span>
                </div>
                <div className="space-y-2">
                  {leads.map(lead => {
                    const wa = waLink(lead.whatsapp || lead.phone);
                    const time = new Date(lead.next_contact_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
                    return (
                      <div key={lead.id} className={`p-3 rounded-lg border ${isOverdue ? "border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900/40" : "border-[#E5E5E5] dark:border-white/10"} flex items-center justify-between gap-3 flex-wrap`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-mono text-neutral-500">{time}</span>
                            <span className="font-medium text-sm truncate">{lead.name}</span>
                          </div>
                          {lead.interest && <p className="text-[11px] text-neutral-500 truncate">{lead.interest}</p>}
                        </div>
                        <div className="flex items-center gap-1.5">
                          {wa && <a href={wa} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded text-emerald-600" title="WhatsApp"><MessageCircle size={14}/></a>}
                          {lead.phone && <a href={`tel:${lead.phone}`} className="p-1.5 hover:bg-neutral-100 dark:hover:bg-white/5 rounded" title="Ligar"><Phone size={14}/></a>}
                          <Button size="sm" variant="outline" className="text-xs h-7" onClick={()=>markContact(lead)} data-testid={`agenda-mark-${lead.id}`}>Realizado</Button>
                          <Button size="sm" variant="ghost" className="text-xs h-7" onClick={()=>{ onClose(); onOpenLead(lead.id); }}>Abrir</Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}



// ─────────────────────────────────────────────────────────────────────────────
// Dialog: Gerenciar etapas — criar / reordenar (↑ ↓) / renomear / cor / excluir
// ─────────────────────────────────────────────────────────────────────────────
const DEFAULT_COLORS = [
  "#94A3B8", "#0EA5E9", "#22C55E", "#F59E0B", "#EF4444",
  "#A855F7", "#EC4899", "#14B8A6", "#F97316", "#6366F1",
];

function StageManagerDialog({ open, onClose, stages, onChanged }) {
  const [list, setList] = useState(stages || []);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(DEFAULT_COLORS[0]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setList(stages || []); }, [stages, open]);

  const persistOrder = async (arr) => {
    setBusy(true);
    try {
      await api.put("/crm/stages/reorder", { order_ids: arr.map(s => s.id) });
      await onChanged();
    } catch { toast.error("Erro ao reordenar"); }
    finally { setBusy(false); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const arr = [...list];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setList(arr);
    await persistOrder(arr);
  };

  const create = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Informe o nome da etapa"); return; }
    setBusy(true);
    try {
      await api.post("/crm/stages", { name, color: newColor });
      setNewName(""); setNewColor(DEFAULT_COLORS[0]);
      toast.success("Etapa criada");
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao criar"); }
    finally { setBusy(false); }
  };

  const rename = async (stage, name) => {
    const v = (name || "").trim();
    if (!v || v === stage.name) return;
    setBusy(true);
    try {
      await api.put(`/crm/stages/${stage.id}`, { name: v, color: stage.color });
      toast.success("Etapa renomeada");
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao renomear"); }
    finally { setBusy(false); }
  };

  const changeColor = async (stage, color) => {
    setBusy(true);
    try {
      await api.put(`/crm/stages/${stage.id}`, { name: stage.name, color });
      await onChanged();
    } catch { toast.error("Erro ao alterar cor"); }
    finally { setBusy(false); }
  };

  const remove = async (stage) => {
    if (!confirm(`Excluir a etapa "${stage.name}"? Os leads dessa etapa serão movidos para a primeira etapa restante.`)) return;
    setBusy(true);
    try {
      await api.delete(`/crm/stages/${stage.id}`);
      toast.success("Etapa excluída");
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao excluir"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="stage-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><TagIcon size={18}/> Gerenciar etapas do funil</DialogTitle>
        </DialogHeader>

        {/* Nova etapa */}
        <Card className="p-3 border-[#E5E5E5]">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Nova etapa</div>
          <div className="flex gap-2 items-center flex-wrap">
            <Input
              value={newName}
              onChange={e=>setNewName(e.target.value)}
              placeholder="Ex.: Aguardando pagamento"
              className="flex-1 min-w-[180px] h-9 text-sm"
              data-testid="stage-new-name"
              onKeyDown={(e)=>{ if (e.key === "Enter") create(); }}
            />
            <div className="flex items-center gap-1">
              {DEFAULT_COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={()=>setNewColor(c)}
                  className={`w-5 h-5 rounded-full border-2 transition ${newColor === c ? "border-neutral-900 dark:border-white scale-110" : "border-transparent"}`}
                  style={{ background: c }}
                  data-testid={`stage-new-color-${c}`}
                  title={c}
                />
              ))}
            </div>
            <Button size="sm" onClick={create} disabled={busy || !newName.trim()} data-testid="stage-create-btn">
              <Plus size={14} className="mr-1"/> Adicionar
            </Button>
          </div>
        </Card>

        {/* Lista */}
        <div className="space-y-2 mt-3">
          {list.map((s, idx) => (
            <StageRow
              key={s.id}
              stage={s}
              canUp={idx > 0}
              canDown={idx < list.length - 1}
              onUp={()=>move(idx, -1)}
              onDown={()=>move(idx, +1)}
              onRename={(name)=>rename(s, name)}
              onChangeColor={(c)=>changeColor(s, c)}
              onDelete={()=>remove(s)}
              disabled={busy}
            />
          ))}
          {list.length === 0 && <div className="text-center text-xs text-neutral-500 py-6">Nenhuma etapa cadastrada.</div>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="stage-manager-close">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StageRow({ stage, canUp, canDown, onUp, onDown, onRename, onChangeColor, onDelete, disabled }) {
  const [name, setName] = useState(stage.name);
  const [showColors, setShowColors] = useState(false);
  useEffect(() => { setName(stage.name); }, [stage.name]);
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg border border-[#E5E5E5] dark:border-white/10" data-testid={`stage-row-${stage.id}`}>
      <div className="flex flex-col">
        <button
          type="button"
          disabled={!canUp || disabled}
          onClick={onUp}
          className="p-0.5 disabled:opacity-30 hover:text-emerald-600"
          title="Subir"
          data-testid={`stage-up-${stage.id}`}
        ><ChevronLeft size={14} style={{ transform: "rotate(90deg)" }}/></button>
        <button
          type="button"
          disabled={!canDown || disabled}
          onClick={onDown}
          className="p-0.5 disabled:opacity-30 hover:text-emerald-600"
          title="Descer"
          data-testid={`stage-down-${stage.id}`}
        ><ChevronRight size={14} style={{ transform: "rotate(90deg)" }}/></button>
      </div>
      <div className="relative">
        <button
          type="button"
          onClick={()=>setShowColors(v => !v)}
          className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
          style={{ background: stage.color }}
          title="Alterar cor"
          data-testid={`stage-color-btn-${stage.id}`}
        />
        {showColors && (
          <div className="absolute z-10 top-8 left-0 p-2 rounded-lg border bg-white dark:bg-neutral-900 shadow-lg flex gap-1 flex-wrap w-40">
            {DEFAULT_COLORS.map(c => (
              <button
                key={c}
                type="button"
                onClick={()=>{ onChangeColor(c); setShowColors(false); }}
                className="w-5 h-5 rounded-full border"
                style={{ background: c }}
              />
            ))}
          </div>
        )}
      </div>
      <Input
        value={name}
        onChange={e=>setName(e.target.value)}
        onBlur={()=>onRename(name)}
        onKeyDown={(e)=>{ if (e.key === "Enter") e.currentTarget.blur(); }}
        className="h-8 text-sm flex-1"
        data-testid={`stage-name-${stage.id}`}
      />
      {stage.is_won && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Ganho</span>}
      {stage.is_lost && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800">Perdido</span>}
      <Button variant="outline" size="sm" onClick={onDelete} disabled={disabled} className="text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30" data-testid={`stage-delete-${stage.id}`}>
        <Trash2 size={12}/>
      </Button>
    </div>
  );
}
